/**
 * Gamut Utilities for Educational Color Space Visualization
 * Provides gamut boundary calculations and color space comparisons
 */

import { colorEngine } from '../colorEngine';

export interface ColorSpace {
  name: string;
  primaries: {
    red: { x: number; y: number };
    green: { x: number; y: number };
    blue: { x: number; y: number };
    white: { x: number; y: number };
  };
  gamma: number;
  description: string;
}

export interface GamutBoundary {
  points: { x: number; y: number; color: string }[];
  area: number;
  coverage: number; // percentage of reference space
}

export interface GamutComparison {
  primary: ColorSpace;
  secondary: ColorSpace;
  overlap: number; // percentage
  primaryOnly: GamutBoundary;
  secondaryOnly: GamutBoundary;
  intersection: GamutBoundary;
}

// Standard color space definitions
export const COLOR_SPACES: Record<string, ColorSpace> = {
  sRGB: {
    name: 'sRGB',
    primaries: {
      red: { x: 0.64, y: 0.33 },
      green: { x: 0.30, y: 0.60 },
      blue: { x: 0.15, y: 0.06 },
      white: { x: 0.3127, y: 0.3290 }
    },
    gamma: 2.2,
    description: 'Standard RGB color space used by most displays and web content'
  },
  DisplayP3: {
    name: 'Display P3',
    primaries: {
      red: { x: 0.68, y: 0.32 },
      green: { x: 0.265, y: 0.69 },
      blue: { x: 0.15, y: 0.06 },
      white: { x: 0.3127, y: 0.3290 }
    },
    gamma: 2.2,
    description: 'Wide gamut color space used by modern displays and Apple devices'
  },
  AdobeRGB: {
    name: 'Adobe RGB',
    primaries: {
      red: { x: 0.64, y: 0.33 },
      green: { x: 0.21, y: 0.71 },
      blue: { x: 0.15, y: 0.06 },
      white: { x: 0.3127, y: 0.3290 }
    },
    gamma: 2.2,
    description: 'Professional photography and printing color space'
  },
  Rec2020: {
    name: 'Rec. 2020',
    primaries: {
      red: { x: 0.708, y: 0.292 },
      green: { x: 0.170, y: 0.797 },
      blue: { x: 0.131, y: 0.046 },
      white: { x: 0.3127, y: 0.3290 }
    },
    gamma: 2.2,
    description: 'Ultra-wide gamut for 4K/8K television and cinema'
  }
};

// Convert RGB to XY chromaticity coordinates
export function rgbToXY(r: number, g: number, b: number): { x: number; y: number } {
  // Normalize RGB values to 0-1 range
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  // Apply gamma correction (sRGB gamma = 2.2)
  const rLinear = rn <= 0.04045 ? rn / 12.92 : Math.pow((rn + 0.055) / 1.055, 2.4);
  const gLinear = gn <= 0.04045 ? gn / 12.92 : Math.pow((gn + 0.055) / 1.055, 2.4);
  const bLinear = bn <= 0.04045 ? bn / 12.92 : Math.pow((bn + 0.055) / 1.055, 2.4);

  // Convert to XYZ
  const X = rLinear * 0.4124564 + gLinear * 0.3575761 + bLinear * 0.1804375;
  const Y = rLinear * 0.2126729 + gLinear * 0.7151522 + bLinear * 0.0721750;
  const Z = rLinear * 0.0193339 + gLinear * 0.1191920 + bLinear * 0.9503041;

  // Convert XYZ to xyY chromaticity
  const sum = X + Y + Z;
  if (sum === 0) return { x: 0, y: 0 };

  return {
    x: X / sum,
    y: Y / sum
  };
}

// Generate gamut boundary points for a color space
export function generateGamutBoundary(colorSpace: ColorSpace, resolution: number = 100): GamutBoundary {
  const points: { x: number; y: number; color: string }[] = [];
  
  // Generate boundary by sampling edges of the color space triangle
  const { red, green, blue } = colorSpace.primaries;
  
  // Red to Green edge
  for (let i = 0; i <= resolution; i++) {
    const t = i / resolution;
    const x = red.x + t * (green.x - red.x);
    const y = red.y + t * (green.y - red.y);
    const hue = Math.atan2(y - colorSpace.primaries.white.y, x - colorSpace.primaries.white.x);
    const color = `hsl(${(hue * 180 / Math.PI + 360) % 360}, 100%, 50%)`;
    points.push({ x, y, color });
  }
  
  // Green to Blue edge
  for (let i = 0; i <= resolution; i++) {
    const t = i / resolution;
    const x = green.x + t * (blue.x - green.x);
    const y = green.y + t * (blue.y - green.y);
    const hue = Math.atan2(y - colorSpace.primaries.white.y, x - colorSpace.primaries.white.x);
    const color = `hsl(${(hue * 180 / Math.PI + 360) % 360}, 100%, 50%)`;
    points.push({ x, y, color });
  }
  
  // Blue to Red edge
  for (let i = 0; i <= resolution; i++) {
    const t = i / resolution;
    const x = blue.x + t * (red.x - blue.x);
    const y = blue.y + t * (red.y - blue.y);
    const hue = Math.atan2(y - colorSpace.primaries.white.y, x - colorSpace.primaries.white.x);
    const color = `hsl(${(hue * 180 / Math.PI + 360) % 360}, 100%, 50%)`;
    points.push({ x, y, color });
  }
  
  // Calculate area using shoelace formula
  const area = calculatePolygonArea(points);
  
  // Calculate coverage relative to sRGB (reference)
  const srgbArea = calculatePolygonArea(generateGamutBoundary(COLOR_SPACES.sRGB, resolution).points);
  const coverage = (area / srgbArea) * 100;
  
  return { points, area, coverage };
}

// Calculate polygon area using shoelace formula
function calculatePolygonArea(points: { x: number; y: number }[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  return Math.abs(area / 2);
}

// Compare two color spaces
export function compareColorSpaces(primary: ColorSpace, secondary: ColorSpace): GamutComparison {
  const primaryBoundary = generateGamutBoundary(primary);
  const secondaryBoundary = generateGamutBoundary(secondary);
  
  // Simple overlap calculation (approximation)
  const overlap = calculateOverlap(primaryBoundary.points, secondaryBoundary.points);
  
  return {
    primary,
    secondary,
    overlap,
    primaryOnly: primaryBoundary,
    secondaryOnly: secondaryBoundary,
    intersection: {
      points: [],
      area: 0,
      coverage: overlap
    }
  };
}

// Calculate overlap between two gamuts (simplified)
function calculateOverlap(points1: { x: number; y: number }[], points2: { x: number; y: number }[]): number {
  // This is a simplified calculation - true polygon intersection is complex
  // For educational purposes, we'll use an approximation based on shared area

  // Estimate overlap based on primary positions (simplified)
  const avgDistance = calculateAverageDistance(points1, points2);
  // const maxDistance = 0.3; // Maximum typical distance between color space primaries
  const overlapFactor = Math.max(0, 1 - (avgDistance / 0.3));

  return overlapFactor * 100;
}

// Calculate average distance between two point sets
function calculateAverageDistance(points1: { x: number; y: number }[], points2: { x: number; y: number }[]): number {
  let totalDistance = 0;
  let count = 0;
  
  for (const p1 of points1) {
    for (const p2 of points2) {
      const distance = Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
      totalDistance += distance;
      count++;
    }
  }
  
  return count > 0 ? totalDistance / count : 0;
}

// Check if a color is within a specific gamut
export function isColorInGamut(color: string, colorSpace: ColorSpace): boolean {
  const parsed = colorEngine.parseColor(color);
  if (!parsed?.rgb) return false;
  
  const { r, g, b } = parsed.rgb;
  const xy = rgbToXY(r, g, b);
  
  // Check if point is inside triangle using barycentric coordinates
  const { red, green, blue } = colorSpace.primaries;
  
  const denominator = ((green.y - blue.y) * (red.x - blue.x) + (blue.x - green.x) * (red.y - blue.y));
  if (Math.abs(denominator) < 0.0001) return false;
  
  const a = ((green.y - blue.y) * (xy.x - blue.x) + (blue.x - green.x) * (xy.y - blue.y)) / denominator;
  const bCoord = ((blue.y - red.y) * (xy.x - blue.x) + (red.x - blue.x) * (xy.y - blue.y)) / denominator;
  const c = 1 - a - bCoord;
  
  return a >= 0 && bCoord >= 0 && c >= 0;
}

// Get gamut limitations explanation
export function getGamutExplanation(colorSpace: ColorSpace): string {
  switch (colorSpace.name) {
    case 'sRGB':
      return 'sRGB is the standard color space for web content and most consumer displays. It provides good color reproduction but has limitations in representing highly saturated greens and reds compared to wider gamuts.';
    case 'Display P3':
      return 'Display P3 offers approximately 25% more color coverage than sRGB, particularly in the green and red regions. It\'s used by modern smartphones, tablets, and high-end displays for more vibrant colors.';
    case 'Adobe RGB':
      return 'Adobe RGB was designed for professional photography and printing. It provides better coverage in the cyan-green region but less in the red region compared to Display P3.';
    case 'Rec. 2020':
      return 'Rec. 2020 is an ultra-wide gamut standard for 4K/8K television and cinema. It can display colors far beyond what current consumer monitors can reproduce, representing the future of color technology.';
    default:
      return `${colorSpace.name} color space with specific characteristics and limitations.`;
  }
}

// Generate educational gamut examples
export function getGamutExamples(): Array<{
  name: string;
  description: string;
  colors: string[];
  colorSpace: string;
  explanation: string;
}> {
  return [
    {
      name: 'Vibrant Green Foliage',
      description: 'Deep forest greens that challenge sRGB limits',
      colors: ['#006400', '#228B22', '#32CD32', '#00FF00'],
      colorSpace: 'Display P3',
      explanation: 'These natural greens exceed sRGB gamut but are well within Display P3, showing why wide gamut displays better represent nature.'
    },
    {
      name: 'Sunset Reds',
      description: 'Warm sunset colors that push gamut boundaries',
      colors: ['#FF6B35', '#FF4500', '#DC143C', '#B22222'],
      colorSpace: 'Adobe RGB',
      explanation: 'These warm reds benefit from Adobe RGB\'s extended red gamut, though some may still be limited.'
    },
    {
      name: 'Ocean Blues',
      description: 'Deep ocean and sky blues',
      colors: ['#000080', '#4169E1', '#1E90FF', '#00BFFF'],
      colorSpace: 'sRGB',
      explanation: 'These blues are well-represented in sRGB, showing that not all colors benefit from wider gamuts.'
    },
    {
      name: 'Professional Printing',
      description: 'Colors optimized for print reproduction',
      colors: ['#0047AB', '#FFD700', '#FF6347', '#8B4513'],
      colorSpace: 'Adobe RGB',
      explanation: 'These colors are chosen for Adobe RGB\'s strengths in professional printing workflows.'
    }
  ];
}
